
import { useEffect, useState } from 'react'
import { CHAT_FONT_FAMILIES } from 'lib/chatFontCatalog'
import { normalizeChatFontKey } from 'lib/chatFonts'

const { VITE_FONTS_URL: fontsUrl } = import.meta.env

const FONT_WAIT_CAP_MS = 5000

const fontsBaseUrl = (): string | null => {
  if (typeof fontsUrl !== 'string' || fontsUrl === '') return null
  return fontsUrl
}

const preconnectFontsCdn = (): void => {
  const base = fontsBaseUrl()
  if (base === null || typeof document === 'undefined') return
  try {
    const { origin } = new URL(base)
    if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`) !== null) return
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = origin
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
  } catch {
  }
}
preconnectFontsCdn()

export const fontLoadRequired = (key: string): boolean => {
  return normalizeChatFontKey(key) !== 'DEFAULT' && fontsBaseUrl() !== null
}

let cssPromise: Promise<void> | null = null

const ensureCatalogCss = async (base: string): Promise<void> => {
  if (cssPromise === null) {
    cssPromise = new Promise<void>((resolve) => {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = `${base}/fonts.css`
      link.onload = () => resolve()
      link.onerror = () => resolve()
      document.head.appendChild(link)
    })
  }
  return await cssPromise
}

const familyLoads = new Map<string, Promise<void>>()

const boundedWait = async (work: Promise<unknown>): Promise<void> => {
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined
  const cap = new Promise<void>((resolve) => {
    timer = globalThis.setTimeout(resolve, FONT_WAIT_CAP_MS)
  })
  try {
    await Promise.race([work, cap])
  } finally {
    globalThis.clearTimeout(timer)
  }
}

export const ensureFontLoaded = async (key: string): Promise<void> => {
  const normalized = normalizeChatFontKey(key)
  if (normalized === 'DEFAULT') return
  const base = fontsBaseUrl()
  if (base === null) return
  const cached = familyLoads.get(normalized)
  if (cached !== undefined) return await cached
  const family = CHAT_FONT_FAMILIES[normalized]
  const load = boundedWait((async () => {
    await ensureCatalogCss(base)
    if (typeof document === 'undefined' || document.fonts === undefined) return
    await Promise.all(
      family.weights.map(async (w) =>
        await document.fonts.load(`${w} 16px "${family.family}"`))
    )
  })().catch(() => {}))
  familyLoads.set(normalized, load)
  return await load
}

export const useFontGate = (key: string | null): boolean => {
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const gated = key !== null && fontLoadRequired(key)
  useEffect(() => {
    if (key === null || !gated) return
    let cancelled = false
    void ensureFontLoaded(key).then(() => {
      if (!cancelled) setLoadedKey(key)
    })
    return () => { cancelled = true }
  }, [key, gated])
  return !gated || loadedKey === key
}
